import { type Account, accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import type { SplitItemProps, SplitProps } from 'app-components/flux/transactions/transactionsTypes';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';
import type { Map as ImMap } from 'immutable';
import type { Category } from '../categories/categoriesTypes';
import * as chartOfAccountsTypes from './chartOfAccountsTypes';

export const isCoaSettable = (coa: chartOfAccountsTypes.ChartOfAccountProps) =>
  coa.type !== chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE &&
  coa.type !== chartOfAccountsTypes.CoaTypeEnum.DISPLAY_NODE;

export function getCoaId(coa?: { id?: string; type?: string } | null, allowBlank = false) {
  if (!coa || !coa.id) {
    return allowBlank ? 'BLANK' : chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED;
  }
  switch (coa.type) {
    case chartOfAccountsTypes.CoaTypeEnum.ACCOUNT:
      return coa.id;
    case chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT:
      return `${chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT}_${coa.id}`;
    case chartOfAccountsTypes.CoaTypeEnum.CATEGORY:
      return coa.id;
    case chartOfAccountsTypes.CoaTypeEnum.NONE:
      return coa.type;
    case chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED:
    default:
      return chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED;
  }
}

export function getBalanceAdjustmentString(id: string) {
  switch (id) {
    case chartOfAccountsTypes.BalanceAdjustmentTypeEnum.BALANCE_ADJUSTMENT:
      return 'Balance Adjustment';
    case chartOfAccountsTypes.BalanceAdjustmentTypeEnum.OPENING_BALANCE:
      return 'Opening Balance';
    case chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER:
      return 'Transfer';
    case chartOfAccountsTypes.BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT:
      return 'Credit Card Payment';
    default:
      assert(false, 'unknown coa.id for coa.type=BALANCE_ADJUSTMENT');
      return `ErrId:=>${String(id)}<=`;
  }
}

export const coaIsBalanceAdjustmentType = (coa?: chartOfAccountsTypes.ChartOfAccountProps | null) =>
  coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT;

export function isL1TransferCoa(coa?: chartOfAccountsTypes.ChartOfAccountProps | null) {
  return (
    coa &&
    coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT &&
    coa.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER
  );
}

export const TRANSFER_NOT_FOUND_COA_ID = '24';
export const unsupportedTypes = ['INVESTMENT', 'LOAN'];

export function coasAreEqual(
  coa1?: chartOfAccountsTypes.ChartOfAccountProps | null,
  coa2?: chartOfAccountsTypes.ChartOfAccountProps | null,
) {
  if (!coa1 && !coa2) return true;
  if (!coa1 || !coa2) return false;
  return coa1.type === coa2.type && coa1.id === coa2.id;
}

// we don't support transactions for Loan or Investment accounts
// so we treat them as NOT FOUND
export function isTransferCoaNotFound(
  coa?: chartOfAccountsTypes.ChartOfAccountProps | null,
  state: RootState = store!.getState(),
) {
  const accountsById = accountsSelectors.getAccountsByIdIncludingGoals(state);

  if (coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) {
    return accountsById && !accountsById.has(coa.id);
  }
  return false;
}

export function isUnsyncedTransferCoa(
  coa: chartOfAccountsTypes.ChartOfAccountProps | null | undefined,
  state?: RootState,
) {
  if (coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) {
    return isTransferCoaNotFound(coa, state);
  }
  return false;
}

export function isLoanAcctTransferCoa(
  coa: chartOfAccountsTypes.ChartOfAccountProps | null | undefined,
  state: RootState,
): boolean {
  if (coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) {
    return accountsUtils.isLoanAccountFromId(coa.id, state);
  }
  return false;
}

export function isInvestmentAcctTransferCoa(
  coa: chartOfAccountsTypes.ChartOfAccountProps | null | undefined,
  state: RootState,
): boolean {
  if (coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) {
    return accountsUtils.isInvestmentAccountFromId(coa.id, state);
  }
  return false;
}

export function isSupportedTransferCoa(
  coa?: chartOfAccountsTypes.ChartOfAccountProps | null,
  state: RootState = store!.getState(),
) {
  const accountsById = accountsSelectors.getAccountsByIdIncludingGoals(state);
  if (coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) {
    return (
      !isUnsyncedTransferCoa(coa, state) &&
      accountsById &&
      accountsById.has(coa.id) &&
      unsupportedTypes.indexOf(accountsById.get(coa.id)?.type || '') < 0
    );
  }
  return false;
}

export const isTransferCOA = (coa?: chartOfAccountsTypes.ChartOfAccountProps | null) =>
  coa &&
  (coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT ||
    coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT);

const isCategory = (splitTxn: SplitItemProps) => splitTxn.coa?.type === 'CATEGORY';

export const disableExcludeReports = (
  coa?: chartOfAccountsTypes.ChartOfAccountProps | null,
  split?: SplitProps | null,
) => {
  if (!split || !split.items) {
    return coa?.type === 'BALANCE_ADJUSTMENT' && (coa?.id === '0' || coa?.id === '1');
  }

  if (Array.isArray(split.items)) {
    return split.items.filter(isCategory).length === 0;
  }

  return split.items.filter(isCategory).size === 0;
};

export const getCoaString = (
  coa: chartOfAccountsTypes.ChartOfAccountProps | null | undefined,
  categoriesById: ImMap<string, Category>,
  accountsById: ImMap<string, Account>,
  noParent = false,
) => {
  let coaString;
  switch (coa?.type) {
    case chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED:
      coaString = 'Uncategorized';
      break;
    case chartOfAccountsTypes.CoaTypeEnum.ACCOUNT:
      {
        const acct = accountsById?.get(coa.id);
        coaString = acct?.name || 'Unsynced Account';
      }
      break;
    case chartOfAccountsTypes.CoaTypeEnum.CATEGORY:
      coaString = categoriesSelectors.getCategoryString(categoriesById, coa.id, noParent);
      break;
    case chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT:
      coaString = getBalanceAdjustmentString(coa.id);
      break;
    default:
      coaString = 'NA';
  }
  return coaString;
};
